import { useEffect, useState } from "react";
import axios from "axios";
import { useForm, Controller, useWatch } from "react-hook-form";
import { InputAdornment, TextField } from "@mui/material";
import { NumericFormat } from "react-number-format";
import { Box, Save } from "react-feather";
import { authData } from "../../components/GetAuth";
import { gbp } from "../../../utils/bookingCharges";
import { calculateDimensionTotal, dimensionBreakdown, resolveDimensionPricing } from "../../../utils/dimensionPricing";
import styles from "./companyprofile.module.css";

const toFormPricing = (pricing) => {
  const value = resolveDimensionPricing(pricing);
  Object.keys(value).forEach((k) => {
    value[k] = value[k] === null || value[k] === undefined ? "" : String(value[k]);
  });
  return value;
};

const NumberField = ({ control, errors, name, label, required, money, unit, decimals = 2, hint, positive }) => {
  const error = errors?.[name];
  return (<div>
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (v) => {
          if (v === "" || v === null || v === undefined) return !required || `${label.replace("*", "")} is required`;
          if (positive && Number(v) <= 0) return `${label.replace("*", "")} must be more than 0`;
          return true;
        },
      }}
      render={({ field: { onChange, ...field } }) => (
        <NumericFormat
          {...field}
          onValueChange={(v) => onChange(v.value)}
          customInput={TextField}
          label={label}
          variant="outlined"
          fullWidth
          allowNegative={false}
          decimalScale={decimals}
          inputMode="decimal"
          error={!!error}
          InputProps={{
            ...(money ? { startAdornment: <InputAdornment position="start">£</InputAdornment> } : {}),
            ...(unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : {}),
          }}
        />
      )}
    />
    {error
      ? <span className={`${styles.FieldError}`}>{error.message}</span>
      : hint && <span className={`${styles.FieldHint}`}>{hint}</span>}
  </div>);
};

const DimensionPricingSettings = ({ pricing, onSaved, showMessage }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [saving, setSaving] = useState(false);
  const [example, setExample] = useState({ length: "100", width: "100", height: "100" });

  const { handleSubmit, control, reset, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: toFormPricing(null),
  });

  useEffect(() => {
    reset(toFormPricing(pricing));
  }, [pricing, reset]);

  const values = useWatch({ control });
  const total = calculateDimensionTotal(values, example.length, example.width, example.height);

  const onSubmit = (data) => {
    setSaving(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/company-settings/dimension-pricing", data, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        showMessage(response.data.message, "success");
        onSaved(response.data.data);
      } else {
        showMessage(response.data.message, "error");
      }
    }).catch(() => {
      setSaving(false);
      showMessage("Could not save dimension pricing. Please try again.", "error");
    });
  };

  const exampleInput = (key, label) => (
    <TextField size="small" className={`${styles.EstimatorInput}`} value={example[key]}
      onChange={(e) => { const v = e.target.value.replace(/[^\d.]/g, ""); setExample((s) => ({ ...s, [key]: v })); }}
      inputProps={{ inputMode: "decimal", "aria-label": `Example ${label} in cm` }} />
  );

  return (<form onSubmit={handleSubmit(onSubmit)} noValidate>
    <section className={`${styles.Card}`}>
      <div className={`${styles.CardHead}`}>
        <div>
          <h2 className={`${styles.CardTitle}`}><Box />Dimension pricing</h2>
          <p className={`${styles.CardSubtitle}`}>Price of "Other With Dimension" items, worked out from the length, width and height entered on every booking form.</p>
        </div>
      </div>

      <div className={`${styles.CardBody}`}>
        <div className={`${styles.FormGrid}`}>
          <NumberField control={control} errors={errors} name="unit_price" label="Price per unit*" money required hint="Charged for every unit of volume." />
          <NumberField control={control} errors={errors} name="unit_volume" label="Unit volume*" unit="m³" decimals={4} required positive hint="Size of one priced unit, e.g. a standard box." />
          <NumberField control={control} errors={errors} name="extra_volume" label="Extra volume allowance" unit="m³" decimals={4} hint="Optional. Added to every item's volume (e.g. packing)." />
          <NumberField control={control} errors={errors} name="minimum_charge" label="Minimum charge" money hint="Optional. Lowest amount ever charged per item." />
        </div>

        <div className={`${styles.Estimator}`}>
          <div className={`${styles.EstimatorHead}`}>
            <span>Example:</span>
            {exampleInput("length", "length")}
            <span>×</span>
            {exampleInput("width", "width")}
            <span>×</span>
            {exampleInput("height", "height")}
            <span>cm</span>
            <span className={`${styles.EstimatorTotal}`}>{gbp(total)}</span>
          </div>
          <p className={`${styles.EstimatorBreakdown}`}>{dimensionBreakdown(values, example.length, example.width, example.height)}</p>
        </div>
      </div>
    </section>
    <div className={`${styles.CardFooter}`} style={{ borderTop: "none", padding: "18px 0 0" }}>
      <button type="submit" className={`${styles.ActionBU} ${styles.ActionPrimary}`} disabled={saving}><Save /> {saving ? "Saving..." : "Save Dimension Pricing"}</button>
    </div>
  </form>);
};

export default DimensionPricingSettings;
